type Mutation {
  verifyDocument(input: DocumentVerificationInput!, extendedFingerprint: Boolean): DocumentVerification
  verifyDocuments(input: [DocumentVerificationInput!]!): [DocumentVerification]
  uploadDocument(input: DocumentUploadInput!, verifyOnUpload: Boolean): DocumentEntity
  uploadSignedCopy(documentId: ID!, input: UploadSignedCopyInput!): DocumentEntity
  updateDocument(id: ID!, input: DocumentInput!, isArchiving: Boolean, mortgageStatus: MortgageStatus): DocumentEntity
  renderAndStoreDocument(input: RenderAndStoreDocumentInput!): DocumentEntity
  renderAndStoreIddDocument(
    input: RenderAndStoreDocumentInput!
    caseId: String
    clientIdsAccept: [String!]
  ): [DocumentEntity]!
  uploadDocumentList(input: [DocumentUploadInput!]!): [DocumentEntity!]!
  checkMistralDocuments(input: MistralInput!): MistralResponse
  uploadDocumentClassification(
    input: ClassificationUploadDocumentInput!
    verifyOnUpload: Boolean
  ): ClassificationUploadDocumentResponse

  createAuthProvider(input: AuthProviderInput!): AuthProvider
  replaceAuthProvider(id: ID!, input: AuthProviderInput!): AuthProvider
  updateAuthProvider(id: ID!, input: AuthProviderInput!): AuthProvider

  # Browser ID
  postBrowserId(useClientApi: Boolean): BrowserId

  # Case
  createCase(input: CaseInput!): Case
  replaceCase(id: ID!, input: CaseInput!): Case
  createReferralCase(input: ReferralCaseInput!): CaseVersion
  updateCase(id: ID!, input: CaseInput!): CaseVersion
  updateCaseCp(id: ID!, input: CaseInput!): CaseVersion
  updateCaseCpSurvey(id: ID!, input: AnswerInput!): SurveyAnswer
  addMortgageToCase(id: ID!, mortgageId: ID!): MortgageAndCase
  addCase(input: CaseInput!): CaseVersion
  updateCasePreferences(id: ID!, input: CasePreferencesInput!): CaseVersion
  updateCaseFees(id: ID!, input: UpdateCaseFeeInput!): CaseVersion
  updateCaseRequirements(id: ID!, input: CaseRequirementsInput!): CaseVersion
  updateCaseAssignees(input: CaseAssigneesInput): CaseAssignees
  bulkCreateRemoLeads(input: BulkCreateRemoLeadsInput): BulkCreateRemoLeads
  createCaseFlagReview(input: CaseFlagReviewInput!): CaseVersion
  deleteCaseProperty(
    caseId: ID!
    reasons: [String!]!
    propertyId: ID!
    propertyType: String!
    deleteFromClient: Boolean!
  ): CaseVersion
  deleteCasePropertyCp(
    caseId: ID!
    reasons: [String!]!
    propertyId: ID!
    propertyType: String!
    deleteFromClient: Boolean!
  ): CaseVersion
  updateCaseStatus(id: ID!, input: CaseUpdateStatusInput!): CaseUpdateStatusResponse
  updateCaseSuitability(id: ID!, input: CaseSuitabilityInput!): CaseVersion

  versionClient(id: ID!): ClientVersion
  addExistingPropertyToCase(propertyId: ID!, caseId: ID!, propertyType: String!): CaseVersion

  addCaseProperty(input: PropertyInput!, caseId: ID!, propertyType: String!): CaseVersion

  sourceMortgages(input: MortgageSourcingInput!): SourceResponse!
  addCompanyAndUpdateCase(caseId: ID!, organisationId: ID, input: ClientInput!): CaseVersion

  updateCaseAndAddNote(
    id: ID!
    noteInput: NoteInput
    isIntroducerCase: Boolean
    useClientApi: Boolean
    includeClientNames: Boolean
    caseInput: CaseInput!
    includeCaseDetailsInProperty: Boolean
  ): UpdateCaseAndAddNoteResponse

  createLeadCaseReferral(
    case_id: ID!
    client_ids: [ID!]!
    preference_mortgage_reason: String!
    target_user_id: ID!
    client_generated_lead: Boolean!
  ): LeadCaseReferral

  # Client
  addClient(input: ClientInput!): ClientVersion
  addClientAndUpdateCase(caseId: ID!, newClientId: ID!): ClientVersion
  deleteClientAndUpdateCase(caseId: ID!, deleteClientId: ID!): CaseVersion
  updateClient(id: ID!, input: ClientInput!): ClientVersion
  updateClients(clients: [ClientInput!]!): [ClientVersion!]!
  updateClientAddresses(
    id: ID!
    input: [ClientAddressInput!]!
    client_ids: [String!]
    property_id: String
    useClientApi: Boolean
  ): ClientVersion
  updateClientPreviousNames(id: ID!, input: ClientInput!): ClientVersion
  updateClientIncome(id: ID!, input: [ClientIncomeInput!]!): ClientVersion
  updateClientDeposits(id: ID!, input: [DepositInput!]): ClientVersion
  runIdVerification(id: ID!): Boolean!
  updateClientAccounts(id: ID!, input: [ClientBankAccountInput!]!): ClientVersion
  updateClientDebts(id: ID!, input: [DebtInput!]!): ClientVersion
  updateClientOrganisationRelationship(
    client_id: String!
    organisation_id_or_ext: String!
    input: MarketingPreferencesInput!
  ): [MarketingPreferences!]
  createClientOrganisationRelationship(
    client_id: String!
    organisation_id_or_ext: String!
    input: ClientOrganisationRelationshipInput!
  ): ClientOrganisationRelationship!
  upsertCompany(company: CHCompanyInput!, user_id: String!, organisation_id: String!): ClientVersion
  upsertCompanyAndDirectors(company: CHCompanyInput!, user_id: String!, organisation_id: String!): [ClientVersion!]!
  changeRelationshipType(relatedClientId: ID!, relationshipType: ClientRelationship!, clientId: ID!, caseId: ID): String

  # Credit history
  updateClientCcjs(id: ID!, input: ClientInput!): ClientVersion
  updateClientCreditRefusals(id: ID!, input: ClientInput!): ClientVersion
  updateClientBankruptcyEvents(id: ID!, input: ClientInput!): ClientVersion
  updateClientDefaultEvents(id: ID!, input: ClientInput!): ClientVersion
  updateClientDebtManagementPlans(id: ID!, input: ClientInput!): ClientVersion
  updateClientArrears(id: ID!, input: ClientInput!): ClientVersion
  updateClientDebtManagementPlan(id: ID!, input: ClientInput!): ClientVersion
  updateClientIvas(id: ID!, input: ClientInput!): ClientVersion

  # Organisation
  createOrganisation(input: OrganisationInput!): Organisation!
  addOrganisationFees(id: ID!, input: [FeeOptionInput!]!): Organisation!
  updateOrganisation(id: ID!, input: OrganisationInput!): Organisation!
  setupOrganisation(input: SetupOrganisationInput!): Organisation!

  # User
  updateUserStatus(userId: ID!, status: Boolean!): User!
  updateUser(userId: ID!, input: UserInput!): User!
  createUser(input: UserInput!): User!
  setupUser(input: UserInput!): User!
  changeUserEmail(userId: ID!, newEmail: String!): User!
  resetUserMfa(userId: ID!): ResetMfaResponse!

  # Note
  addNote(input: NoteInput, isIntroducerCase: Boolean, useClientApi: Boolean): Note
  updateNote(note_id: ID!, input: NoteInput, useClientApi: Boolean): Note
  updateMessage(note_id: ID!, input: NoteInput, useClientApi: Boolean): Note
  deleteNote(note_id: ID!): ID

  # Document requirement
  addDocumentRequirement(input: DocumentRequirementInput!): [DocumentRequirement!]
  updateDocumentRequirement(input: DocumentRequirementInput!): [DocumentRequirement!]

  # Messages
  addMessage(input: NoteInput, isIntroducerCase: Boolean, useClientApi: Boolean): Note

  # Mortgage
  deleteMortgage(mortgageId: ID!, caseId: ID!, isPropertyPortfolio: Boolean): MortgageAndCase
  deleteMortgages(mortgageIds: [ID!]!, caseId: ID!, isPropertyPortfolio: Boolean): [MortgageAndCase]!
  createMortgage(caseId: ID, mortgageInput: MortgageInput!, mortgageProductInput: MortgageProductActualInput!): Mortgage
  createMortgageWithProductAndLinkToCase(input: CreateMortgageWithProductInput!): CreateMortgageWithProduct
  updateMortgageAndProduct(mortgageId: ID, input: UpdateMortgageWithProductInput!): MortgageWithProduct

  # Mortgage product
  updateMortgageProduct(
    mortgageId: ID!
    mortgageProductId: ID!
    mortgageInput: MortgageInput!
    mortgageProductInput: MortgageProductActualInput!
  ): Mortgage!

  updateMortgage(id: ID!, input: MortgageInput!): Mortgage
  updateMortgages(input: [UpdateMortgagesInput!]): [Mortgage]!

  addMortgageToProduct(input: MortgageAndProductInput!): MortgageAndProduct!
  saveMortgageProductToCase(input: MortgageProductToCaseInput!): Mortgage

  addProperty(input: PropertyInput!, caseId: ID, reasons: [String!], propertyType: String): PropertyVersion
  addPropertyCp(input: PropertyInput!, caseId: ID, reasons: [String!], propertyType: String): PropertyVersion

  updateProperty(propertyId: ID!, input: PropertyInput!): PropertyVersion
  mutatePropertyAndMortgages(input: MutatePropertyAndMortgagesInput!): MutatePropertyAndMortgagesResponse

  # DIP
  submitDip(payloadInput: DipInput!, queryInput: SubmitDipInput): SubmitDip
  submitNoPropertyDip(payloadInput: NoPropertyDipInput!, queryInput: SubmitDipInput): SubmitDip
  submitLBGDip(payloadInput: DipInput!, queryInput: LBGDipInput!): SubmitLBGDip
  getDipCert(id: ID!, mortgageId: ID, case: ID!): Boolean
  getDipDocument(id: ID!, mortgageId: ID, case: ID!, documentType: DipDocumentType): Boolean
  doFMA(input: FMAInput!): FMAResponse

  # Protection
  saveProtectionProductsToCase(inputs: SaveToCaseInput!): SaveToCaseOutput
  saveProtectionProductsToCaseV2(inputs: SaveToCaseInput!): SaveToCaseOutput
  saveBusinessProtectionProducts(input: BusinessProtectionProductsInput!): [ProtectionProduct]
  saveProtectionProducts(inputs: [ProtectionProductInput!]!): [ProtectionProduct!]
  removeProtectionProducts(protection_ids: [ID!]!, case_id: ID!): [String!]
  runSolutionSourcing(input: RunSolutionSourcingInput!): RunSolutionSourcingResponse
  runSolutionSourcingV2(input: RunSolutionSourcingInput!): RunSolutionSourcingResponse
  updateProtectionProduct(input: ProtectionProductInput, protection_id: String!): ProtectionProduct
  syncProtectionDocuments(input: SyncProtectionDocumentsInput!): ProtectionProduct
  syncProtectionDocumentsV2(input: SyncProtectionDocumentsInput!): ProtectionProduct
  getRiskReport(input: RiskReportInput!): ClientVersion

  # General insurance
  createGeneralInsuranceProduct(input: CreateGeneralInsuranceProductRequestInput!): GeneralInsuranceProductResponse

  updateGeneralInsuranceProduct(id: ID!, input: CreateGeneralInsuranceProductInput!): GeneralInsuranceProductResponse

  addGeneralInsuranceProductCase(
    input: AddGeneralInsuranceProductCaseRequestInput!
  ): GeneralInsuranceProductCaseResponse

  removeGeneralInsuranceProduct(general_insurance_product_id: ID!, case_id: ID!): String

  # Templates
  postClientInvites(
    emailInput: [EmailTemplateInput!]!
    templateInput: TemplateInput!
    organisationPrivacyPayloadInput: TemplateInput!
    creditReportPayloadInput: TemplateInput
    shouldSendEmailInvite: Boolean
  ): [EmailTemplate!]
  postClientInvitesTemplate(emailInput: EmailTemplateInput!): EmailTemplate!
  createOrganisationTemplate(input: OrganisationTemplateInput!): Template

  ### Client Portal
  addClientCp(input: ClientInput!): ClientVersion
  # Client User
  updateClientUser(id: ID!, input: ClientInput!): ClientVersion!
  uploadClientUserDocument(document: DocumentUploadInput!, mortgage_id: String): DocumentEntity
  updateDocumentCp(id: ID!, input: DocumentInput!): DocumentEntity
  verifyClientUserDocument(input: DocumentVerificationInput!): DocumentVerification
  renderAndStoreClientUserDocument(input: RenderAndStoreDocumentInput!): DocumentEntity
  # Auth
  authenticationClientUser(email: String!): AuthMagicLink
  magicLinkCookie(token: String!, clientId: String): AuthMagicLink
  # Onfido
  onfidoToken(id: ID): String
  onfidoCallback(input: OnfidoCallbackInput!): Boolean
  creditCheckCallback(id: ID): Boolean
  addCasePropertyCp(input: PropertyInput!, caseId: ID!, propertyType: String!): CaseVersion
  updatePropertyCp(propertyId: ID!, input: PropertyInput!): PropertyVersion
  # Mortgage
  addMortgageAndProductCp(mortgageInput: MortgageInput!, mortgageProductInput: MortgageProductActualInput!): Mortgage
  updateMortgageCp(id: ID!, input: MortgageInput!): Mortgage
  #Product
  updateMortgageProductCp(
    mortgageId: ID!
    mortgageProductId: ID!
    mortgageProductInput: MortgageProductActualInput!
  ): MortgageProduct!

  # Groups
  createGroup(organisation_id: String, input: CreateGroupInput): Group
  updateGroup(group_id: ID!, input: UpdateGroupInput): Group

  #Accounting
  editLedgerPayment(input: EditLedgerPaymentInput!, ledgerId: ID!): Ledger!
  createLedgerPayment(input: CreateLedgerInput!): OneOffLedger!
  createRecurringPayment(input: CreateRecurringPaymentInput!): RecurringLedger!
  updateRecurringLedgerPayment(input: UpdateRecurringLedgerInput!, ledgerId: ID!): RecurringLedger!
  updateLedgerPayment(input: UpdateLedgerInput!, ledgerId: ID!): Ledger!
  updateLedgersFullPayment(input: PutLedgersFullPaymentInput!): PutLedgersFullPaymentResponse!
  writeOffLedger(input: UpdateLedgerInput!, ledgerId: Int!): Ledger!
  deleteLedger(id: Int!): Int!
  updatePayment(input: LedgerPaymentInput, ledgerId: ID!, paymentId: ID!): LedgerPayment
  initiateGoCardlessPayment(input: InitiateGoCardlessPaymentInput): [LedgerPayment!]
  goCardlessAuthCallback(input: GoCardlessAuthCallbackInput): ID!
  createMandate: CreateMandateResponse
  uploadProviderStatement(input: UploadProviderStatementInput!): UploadProviderStatementResponse!
  downloadProviderStatementTemplate(input: DownloadProviderStatementTemplateInput!): ProviderStatementTemplate!

  #CommissionStructure
  createCommissionStructure(input: CommissionStructureInput!): CommissionStructure!
  updateCommissionStructure(id: ID!, input: CommissionStructureInput!): CommissionStructure!

  # Calendar
  createCalendarEvent(input: CalendarEventInput!): Note
  revokeOauth(id: ID!): ID!

  # Permissions
  createPermissionAssignment(input: PermissionAssignmentInput!): PermissionAssignmentResponse
  createPermissionAssignmentBulk(input: PermissionAssignmentBulkInput): PermissionAssignmentResponse
  updatePermissionAssignmentBulk(input: PermissionAssignmentBulkInput): PermissionAssignmentResponse
  updatePermissionAssignment(id: ID!, input: PermissionAssignmentInput!): PermissionAssignmentResponse
  updatePermissionsList(
    assignmentsToCreate: [PermissionAssignmentInput!]
    assignmentsToDisable: [PermissionAssignmentInput!]
    assignmentsToEnable: [PermissionAssignmentInput!]
    userId: String!
  ): PermissionAssignmentResponse

  # Scheduler
  sendSchedulingInvite(input: SchedulingInviteInput!): SchedulingInvite
  createSchedulerEvent(start_time: Int!, key: String!): SchedulerEvent
  createSchedulerEventDirect(start_time: Int!, caseId: String!, title: String): SchedulerEvent

  # KBA
  submitKbaQuestionnaire(userId: ID!, input: KbaInput!, useClientApi: Boolean): KbaSubmissionResponse
  runQuotationCreditSearch(clientIds: [ID!]!): Boolean

  # Suitability
  sendClientSrNotification(input: SrNotificationInput!): SrNotificationResponse

  # Zendesk
  createReportProblem(input: ZendeskReportProblemInput): Boolean

  # Flags
  addManualFlagToCase(input: AddManualFlagToCaseInput): AddManualFlagToCase
  # Certs
  getAcreDipCertBasedOnLenderDip(case: String!, mortgage: String!): Boolean
  getAcreDipCertBasedOnAffordability(case: String!): Boolean

  # Vulnerability
  createVulnerabilityAssessments(caseID: ID!): VulnerabilityResponse
  clientAnswerVulnerabilityQuestion(answer: ClientVulnerabilityAnswerInput!): ClientVulnerabilityQuestionResponse

  # Notifications
  subscribeWebPush(input: WebPushSubscriptionInput!): WebPushSubscription
  unsubscribeWebPush(id: String!): Boolean!
}
